import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { documentsApi } from '@/lib/documents';
import { AppShell } from '@/components/app/AppShell';
import { AskAboutButton } from '@/components/app/AskAboutButton';
import { useDialogs } from '@/components/app/DialogProvider';

const PENDING_STATUSES = ['pending', 'parsing', 'chunking', 'indexing'];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-paper-100 text-ink-600',
  parsing: 'bg-accent-50 text-accent-700',
  chunking: 'bg-accent-50 text-accent-700',
  indexing: 'bg-accent-50 text-accent-700',
  indexed: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-warn-500/10 text-warn-600',
  tombstoned: 'bg-paper-100 text-ink-400',
};

export function DocsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status');
  const qc = useQueryClient();
  const { confirm, alert } = useDialogs();
  const onMutationError = () =>
    alert({ title: t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' }) as string });
  const del = useMutation({
    mutationFn: (id: string) => documentsApi.remove(wsId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', wsId] }),
    onError: onMutationError,
  });
  const reindex = useMutation({
    mutationFn: (id: string) => documentsApi.reindex(wsId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', wsId] }),
    onError: onMutationError,
  });
  const confirmDelete = async (id: string, title: string) => {
    const ok = await confirm({
      title: t('docs.delete_confirm', { defaultValue: 'Delete “{{title}}”? Its chunks, vectors, and the file are removed everywhere.', title }) as string,
      confirmLabel: t('common.delete', { defaultValue: 'Delete' }) as string,
    });
    if (ok) {
      del.mutate(id);
    }
  };
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['documents', wsId],
    queryFn: () => documentsApi.list(wsId!),
    enabled: !!wsId,
    refetchInterval: (q) => {
      const rows = q.state.data || [];
      const hasPending = rows.some((d) =>
        ['pending', 'parsing', 'chunking', 'indexing'].includes(d.status),
      );
      return hasPending ? 3000 : false;
    },
  });

  const rows = (data ?? []).filter((d) => {
    if (!statusFilter) return true;
    if (statusFilter === 'pending') return PENDING_STATUSES.includes(d.status);
    return d.status === statusFilter;
  });

  return (
    <AppShell
      title={t('docs.title', { defaultValue: 'Documents' })}
      description={t('docs.subtitle', { defaultValue: 'The knowledge plane: every uploaded, ingested, and indexed source.' }) as string}
      maxWidth="max-w-5xl"
      actions={
        <Link
          to={`/app/workspace/${wsId}/upload`}
          className="btn-press inline-flex items-center px-4 py-2 text-sm"
        >
          {t('docs.upload', { defaultValue: 'Upload' })}
        </Link>
      }
    >
      <div className="space-y-4">
        {statusFilter && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-ink-500">{t('docs.filtered_by', { defaultValue: 'Filtered by status:' })}</span>
            <span className={`px-2 py-0.5 rounded-md ${STATUS_COLORS[statusFilter] || 'bg-paper-100 text-ink-600'}`}>{statusFilter}</span>
            <span className="text-ink-400">· {rows.length} {t('docs.matching', { defaultValue: 'matching' })}</span>
            <button
              onClick={() => setSearchParams({})}
              className="text-accent-600 hover:underline"
            >
              {t('common.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        )}
        {isLoading && (
          <div className="card-paper p-10 text-center text-ink-500 text-sm">
            {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        )}

        {isError && (
          <div className="card-paper p-10 text-center text-sm">
            <p className="text-warn-600">{t('common.load_error', { defaultValue: 'Could not load. Please try again.' })}</p>
            <button onClick={() => refetch()} className="btn-ghost px-3 py-1.5 text-xs mt-3">
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        )}

        {!isLoading && !isError && (!data || data.length === 0) && (
          <div className="card-paper p-12 text-center">
            <h2 className="display text-2xl text-ink-900 mb-2">
              {t('docs.empty.heading', { defaultValue: 'No documents yet' })}
            </h2>
            <p className="text-sm text-ink-500 mb-6">
              {t('docs.empty.subheading', { defaultValue: 'Upload a fax, scan, PDF, or paste a text file to build your knowledge plane.' })}
            </p>
            <Link
              to={`/app/workspace/${wsId}/upload`}
              className="btn-press inline-flex items-center px-4 py-2 text-sm"
            >
              {t('docs.upload_cta', { defaultValue: 'Upload your first document' })}
            </Link>
          </div>
        )}

        {!isLoading && data && data.length > 0 && rows.length === 0 && (
          <div className="card-paper p-10 text-center text-sm text-ink-500">
            {t('docs.no_match', { defaultValue: 'No documents with this status.' })}{' '}
            <button onClick={() => setSearchParams({})} className="text-accent-600 hover:underline">
              {t('docs.show_all', { defaultValue: 'Show all' })}
            </button>
          </div>
        )}

        {rows.length > 0 && (
          <div className="card-paper overflow-x-auto p-0">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100">
                  <th className="py-2.5 pl-5 pr-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('docs.col.title', { defaultValue: 'Title' })}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('docs.col.source', { defaultValue: 'Source' })}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('docs.col.status', { defaultValue: 'Status' })}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em] text-right">{t('docs.col.chunks', { defaultValue: 'Chunks' })}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('docs.col.lang', { defaultValue: 'Lang' })}</th>
                  <th className="py-2.5 px-3 font-medium text-[11px] uppercase tracking-[0.12em]">{t('docs.col.ocr', { defaultValue: 'OCR' })}</th>
                  <th className="py-2.5 px-3 pr-5 font-medium text-[11px] uppercase tracking-[0.12em] text-right">{t('common.actions', { defaultValue: 'Actions' })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-b border-ink-50 hover:bg-paper-100/40">
                    <td className="py-2 pl-5 pr-3">
                      <Link to={`/app/workspace/${wsId}/docs/${d.id}`} className="font-medium text-ink-900 hover:underline">
                        {d.title}
                      </Link>
                      {d.error && <div className="text-[10px] text-warn-600">{d.error}</div>}
                    </td>
                    <td className="py-2 px-3 text-xs text-ink-700">{d.sourceKind}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md ${STATUS_COLORS[d.status] || ''}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right num text-ink-900">{d.chunkCount}</td>
                    <td className="py-2 px-3 text-xs text-ink-700">{d.language || '—'}</td>
                    <td className="py-2 px-3 text-xs text-ink-700">{d.ocrKind || '—'}</td>
                    <td className="py-2 px-3 pr-5 text-right whitespace-nowrap">
                      <AskAboutButton
                        wsId={wsId!}
                        seed={d.title}
                        label={t('docs.ask', { defaultValue: 'Ask' }) as string}
                        icon={false}
                        className="text-xs text-accent-600 hover:text-accent-700 mr-3"
                      />
                      <Link
                        to={`/app/workspace/${wsId}/search?seed=${encodeURIComponent(d.title)}`}
                        className="text-xs text-accent-600 hover:text-accent-700 mr-3"
                        title={t('docs.search_inside_hint', { defaultValue: 'Search passages from this document' }) as string}
                      >
                        {t('docs.search_inside', { defaultValue: 'Search' })}
                      </Link>
                      <button
                        onClick={() => reindex.mutate(d.id)}
                        disabled={reindex.isPending}
                        className="text-xs text-accent-600 hover:text-accent-700 disabled:opacity-40 mr-3"
                        title={t('docs.reindex_hint', { defaultValue: 'Re-run ingestion (e.g. for a failed document)' }) as string}
                      >
                        {t('docs.reindex', { defaultValue: 'Re-index' })}
                      </button>
                      <button
                        onClick={() => confirmDelete(d.id, d.title)}
                        disabled={del.isPending}
                        className="text-xs text-rose-600 hover:text-rose-700 disabled:opacity-40"
                        title={t('common.delete', { defaultValue: 'Delete' }) as string}
                      >
                        {t('common.delete', { defaultValue: 'Delete' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-ink-500">
          <button onClick={() => refetch()} className="hover:underline">
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
